import React from "react";
import {Link} from "react-router";
import Icon from "../Icon";
import svgSortAsc from "../../svgs/arrow-up.svg";
import svgSortDesc from "../../svgs/arrow-down.svg";
import Checkbox from "../CheckboxNew";
import { peakifyListQuery } from "../../utils";

function sortByColumnOrder(orders, columnOrder) {
    if (!columnOrder || !columnOrder.length) return orders;
    return [...orders].sort((a, b) => {
        const idxA = columnOrder.indexOf(a.split('.')[0]);
        const idxB = columnOrder.indexOf(b.split('.')[0]);
        return (idxA === -1 ? Infinity : idxA) - (idxB === -1 ? Infinity : idxB);
    });
}

function setOrderOnQuery(q, orderValue) {
    if (!orderValue || (Array.isArray(orderValue) && orderValue.length === 0)) {
        delete q.order;
    } else {
        q.order = orderValue;
    }
}

function buildQuery({query, orders, parts, userParts, elCode}) {
    const q = {...query};
    let isActive = false;
    let directionSort = "";
    let sortPriority = 0;

    const currentIndex = parts.findIndex(([code]) => code === elCode);

    if (currentIndex !== -1) {
        isActive = true;
        const direction = parts[currentIndex][1];
        const userIndex = userParts.findIndex(([code]) => code === elCode);
        sortPriority = userIndex + 1;

        if (direction === "asc") {
            setOrderOnQuery(q, orders.map((x, i) => i === currentIndex ? elCode + ".desc" : x));
            directionSort = "asc";
        } else {
            setOrderOnQuery(q, orders.filter((_, i) => i !== currentIndex));
            directionSort = "desc";
        }
    } else {
        // Append: new column is added as the next sort level;
        // previously clicked columns keep their priority.
        setOrderOnQuery(q, [...orders, elCode + ".asc"]);
    }

    // Matching above runs on the internal column codes; the link itself must
    // only ever show Peak.
    return {q: peakifyListQuery(q), isActive, directionSort, sortPriority};
}

export default class extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            displayDiv: false,
        }
        this.hasScrolledToLastVisited = false;
    }

    componentDidMount() {
        setTimeout(() => {
            this.setState({displayDiv: true});
        }, 1000);
        // Items may already be present on first mount.
        this.maybeScrollLastVisited(this.props);
    }

    componentDidUpdate(prevProps) {
        const hadItems = prevProps.items && prevProps.items.length;
        const hasItems = this.props.items && this.props.items.length;
        if (!hadItems && hasItems) {
            this.maybeScrollLastVisited(this.props);
        }
    }

    maybeScrollLastVisited(props) {
        const {lastVisited, items} = props || this.props;
        if (!lastVisited || this.hasScrolledToLastVisited) return;
        if (!items || !items.length) return;

        // Wait one frame so row layout is ready; do not retry on later re-renders
        // (async Owner cells used to retrigger this and jump the page mid-list).
        requestAnimationFrame(() => {
            this.scrollLastVisitedToView(lastVisited);
        });
    }

    scrollLastVisitedToView(lastVisited) {
        if (!lastVisited || this.hasScrolledToLastVisited) return;

        const element = document.getElementsByClassName('last_visited');
        if (!element[0]) return;

        // Attempt at most once per mount once the row exists.
        this.hasScrolledToLastVisited = true;
        try {
            sessionStorage.removeItem('lastClickedProperty');
        } catch (e) {
            // ignore storage errors
        }

        const eleRect = element[0].getBoundingClientRect();
        if (eleRect.top > window.innerHeight) {
            const middle = eleRect.top - window.innerHeight / 1.9 + eleRect.height / 2;
            window.scrollTo({
                top: middle,
                behavior: "smooth"
            });
        }
    }

    render() {
        const {columns, items, query, classNameModifier, toggleCheckbox, hasSubColumns, toggleAllCheckbox, lastVisited, columnOrder} = this.props;
        const displayDiv = this.state.displayDiv;
        // Normalize: URL may send order as array (order=NAME.asc&order=CITY.desc) or comma-separated string (order=NAME.asc,CITY.desc)
        const rawOrders = query && query.order
            ? (Array.isArray(query.order)
                ? query.order
                : String(query.order).split(',').map((s) => s.trim()).filter(Boolean))
            : [];
        // Use URL/click order so priority 1 = primary sort, 2 = secondary (no re-sort by column position)
        const orders = rawOrders;
        const parts = orders.map((x) => x.split("."));
        // Only sorts that have a header of their own get numbered. A view can carry a
        // sort with no column to click (the default LISTED_DATE order on Current
        // Inventory, ARCIMEDES_ROW_ID), and counting those made the visible badges
        // start at 2.
        const sortableCodes = new Set();
        const collectSortableCode = (col) => {
            if (col && col.code) sortableCodes.add(col.sortColumn || col.code);
        };
        columns.forEach((col) => {
            collectSortableCode(col);
            if (col.subColumns) col.subColumns.forEach(collectSortableCode);
        });
        const userParts = parts.filter(([code]) => sortableCodes.has(code));
        // Clicks build the next order from the levels the user can actually see and
        // click. A hidden default (LISTED_DATE with no header of its own) must not
        // survive a click on an unrelated column — otherwise every click just adds a
        // tiebreaker behind a near-unique date and the list never visibly re-sorts.
        const userOrders = orders.filter((_, i) => sortableCodes.has(parts[i][0]));

        return (
            <React.Fragment>
                <div className={`SortTable_rounded-corner ${classNameModifier ? classNameModifier : ''}${displayDiv ? '' : ' display-none'}`}/>
                <table className={"SortTable" + (classNameModifier ? " SortTable--" + classNameModifier : "")}>
                    <thead>
                    <tr>
                        {columns.map((el, i) => {
                            if (el.code) {
                                const {q, isActive, directionSort, sortPriority} = buildQuery({query, orders: userOrders, parts: userParts, userParts, elCode: `${el.sortColumn?el.sortColumn:el.code}`});
                                return (
                                    <th className={`${isActive ? "SortTable_th--active" : ""} padding-left padding-right`}
                                        key={i}
                                        style={el.columnStyle}>
                                        <Link to={{pathname: document.location.pathname, query: q}}>
                                            <div
                                                className={`SortTable_th_name ${directionSort === "asc" || directionSort === "desc" ? "active" : ""}`}>
                                                <span
                                                    className={`SortTable_th_name_icon top ${directionSort === "asc" ? "active" : ""}`}>
                                                    <Icon svg={svgSortAsc}/>
                                                </span>
                                                {el.name}
                                                {sortPriority > 0 && userParts.length > 1 && (
                                                    <span className="SortTable_th_name_priority">{sortPriority}</span>
                                                )}
                                                <span
                                                    className={`SortTable_th_name_icon bottom ${directionSort === "desc" ? "active" : ""}`}>
                                                    <Icon svg={svgSortDesc}/>
                                                </span>
                                            </div>
                                        </Link>
                                    </th>
                                );
                            } else {
                                return (
                                    <th key={i} className={el.subColumns ? '' : 'padding-left padding-right'}
                                        style={el.columnStyle}>
                                        <div className={el.name === '' && this.props.toggleAllCheckbox ? 'th checkbox' :'th'} style={el.headerStyle}>
                                            {el.name === '' && this.props.toggleAllCheckbox ?
                                                <Checkbox type="checkable"
                                                  name={'selectAll'}
                                                  isSelected={this.props.selectedAll}
                                                  handleClick={() => toggleAllCheckbox({target: {name: 'selectAll',checked:this.props.selectedAll}})}
                                                /> : el.name
                                            }
                                        </div>
                                        {hasSubColumns && el.subColumns &&
                                        <div className='SortTable_subcols'>
                                            {el.subColumns.map((subEl, j) => {
                                                if (subEl.code) {
                                                    const {q, isActive, directionSort, sortPriority} = buildQuery({
                                                        query,
                                                        orders: userOrders,
                                                        parts: userParts,
                                                        userParts,
                                                        elCode: `${subEl.sortColumn?subEl.sortColumn:subEl.code}`,
                                                    });
                                                    return (
                                                        <React.Fragment key={`${i}-${j}`}>
                                                            <Link to={{pathname: document.location.pathname, query: q}}
                                                                  className={`${isActive ? "SortTable_th--active" : ""}`}
                                                                  key={`${i}-${j}`}
                                                                  style={subEl.headerStyle}>
                                                                <div
                                                                    className={`SortTable_th_name ${directionSort === "asc" || directionSort === "desc" ? "active" : ""}`}>
                                                                        <span
                                                                            className={`SortTable_th_name_icon top ${directionSort === "asc" ? "active" : ""}`}>
                                                                            <Icon svg={svgSortAsc}/>
                                                                        </span>
                                                    {subEl.name}
                                                    {sortPriority > 0 && userParts.length > 1 && (
                                                                        <span className="SortTable_th_name_priority">{sortPriority}</span>
                                                                    )}
                                                                    <span
                                                                        className={`SortTable_th_name_icon bottom ${directionSort === "desc" ? "active" : ""}`}>
                                                                            <Icon svg={svgSortDesc}/>
                                                                        </span>
                                                                </div>
                                                            </Link>
                                                            {j !== el.subColumns.length - 1 &&
                                                            <div className="border-right"/>}
                                                        </React.Fragment>
                                                    );
                                                }
                                            })}
                                        </div>
                                        }
                                    </th>
                                );
                            }
                        })}
                    </tr>
                    </thead>
                    <tbody>
                    {
                    items ?
                        items.map((el, index) => {
                            return (
                                <tr key={el.ARCIMEDES_ROW_ID + 'key_'+index}>
                                    {columns.map((col, i) => {
                                        if (i === 0 && el.hasOwnProperty("SELECTED")) {
                                            return (
                                                <td key={i} className={`checkbox-td ${lastVisited == el.ARCIMEDES_ROW_ID + 'key_'+index ? 'last_visited' : ''} `}>
                                                    <Checkbox type="checkable"
                                                              name={index}
                                                              isSelected={el.SELECTED}
                                                              handleClick={() => toggleCheckbox({target: {name: index}})}
                                                    />
                                                </td>
                                            );
                                        }

                                        return (
                                            <td key={i}
                                                style={{textAlign: col.textAlign || "left", ...col.columnStyle}}
                                                className={`padding-left padding-right ${lastVisited == el.ARCIMEDES_ROW_ID + 'key_'+index ? 'last_visited' : ''} ${col.className && col.className}`}>
                                                {!col.subColumns && col.render(el, index)}
                                                {col.subColumns &&
                                                <div className='SortTable_subcols_body'>
                                                    {col.subColumns.map((subCol, j) => {
                                                        return (
                                                            <React.Fragment key={`${i}-${j}`}>
                                                                <div key={`${i}-${j}`}
                                                                     style={{textAlign: subCol.textAlign || "left"}}
                                                                     className={`flex ${j === 0 ? "padding-left" : ""}`}>
                                                                    {subCol.render(el)}
                                                                </div>
                                                                {j !== col.subColumns.length - 1 &&
                                                                <div className="border-right"/>}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </div>
                                                }
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })
                        : null}
                    </tbody>
                </table>
            </React.Fragment>
        );
    }
}
